import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot, User, Loader2, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: 'Namaste! 🙏 Welcome to CriLam Decorative. I\'m your AI assistant - ready to help you with:\n\n• Product recommendations\n• PUR vs Manual pasting comparison\n• Finding dealers near you\n• Technical specifications\n\nHow can I assist you today?',
      options: [
        'Kitchen ke liye best product?',
        'CriLam PUR-Bond™ Technology kya hai?',
        'Dealer near me',
        'Why factory-laminated is better?'
      ]
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Generate session ID on mount
  useEffect(() => {
    const existingSession = sessionStorage.getItem('crilam_chat_session');
    if (existingSession) {
      setSessionId(existingSession);
    } else {
      const newSession = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('crilam_chat_session', newSession);
      setSessionId(newSession);
    }
  }, []);

  const sendMessage = async (text) => {
    if (!text.trim() || isTyping) return;

    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // Add user message
    setMessages(prev => [...prev, { type: 'user', text }]);
    setInputValue('');
    setIsTyping(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: text,
          session_id: sessionId
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error('Chat request failed');
      }

      const data = await response.json();
      
      if (data.success && data.response) {
        setMessages(prev => [...prev, { 
          type: 'bot', 
          text: data.response,
          options: generateQuickOptions(data.response)
        }]);
        if (data.session_id) {
          setSessionId(data.session_id);
          sessionStorage.setItem('crilam_chat_session', data.session_id);
        }
      } else {
        throw new Error('Invalid response');
      }

    } catch (error) {
      if (error.name === 'AbortError') {
        return; // Request was cancelled
      }
      
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: 'I apologize, I\'m having trouble connecting right now. Please try again or contact us directly:\n\n📞 8298297475\n📧 info@crilamdecorative.com',
        options: ['Try again', 'Contact Us']
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const generateQuickOptions = (response) => {
    const lowerResponse = response.toLowerCase();
    
    if (lowerResponse.includes('kitchen') || lowerResponse.includes('gas') || lowerResponse.includes('stove')) {
      return ['See Acrylic Boards', 'Bathroom products', 'Get quote', 'Find dealer'];
    } else if (lowerResponse.includes('pur') || lowerResponse.includes('technology')) {
      return ['Kitchen application', 'Bathroom doors', 'See comparison', 'Contact sales'];
    } else if (lowerResponse.includes('dealer') || lowerResponse.includes('city')) {
      return ['Delhi NCR', 'Mumbai', 'Other city', 'Contact directly'];
    } else if (lowerResponse.includes('price') || lowerResponse.includes('cost')) {
      return ['Get exact quote', 'Bulk inquiry', 'Become dealer', 'View products'];
    } else if (lowerResponse.includes('wardrobe') || lowerResponse.includes('furniture')) {
      return ['See Acrylic Boards', 'CNC Doors', 'Request sample', 'Get quote'];
    }
    
    return ['Kitchen products', 'CriLam PUR-Bond™ Technology', 'Find dealer', 'Contact Us'];
  };

  const handleSend = () => {
    sendMessage(inputValue);
  };

  const handleOptionClick = (option) => {
    if (option === 'Try again') {
      // Resend the last user message
      const lastUserMessage = [...messages].reverse().find(m => m.type === 'user');
      if (lastUserMessage) {
        sendMessage(lastUserMessage.text);
      }
    } else if (option === 'Contact Us') {
      window.location.href = '/contact';
    } else {
      sendMessage(option);
    }
  };

  const formatMessage = (text) => {
    // Convert markdown-like formatting to JSX
    return text.split('\n').map((line, i) => {
      // Bold text
      line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      // Bullet points
      if (line.startsWith('• ') || line.startsWith('- ')) {
        return <div key={i} className="ml-2" dangerouslySetInnerHTML={{ __html: line }} />;
      }
      // Check/cross marks
      if (line.includes('✓') || line.includes('✗') || line.includes('❌') || line.includes('✅')) {
        return <div key={i} dangerouslySetInnerHTML={{ __html: line }} />;
      }
      return <div key={i} dangerouslySetInnerHTML={{ __html: line || '<br/>' }} />;
    });
  };

  return (
    <>
      {/* Chat Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-40 right-6 z-40 w-14 h-14 bg-[#004534] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#004534] transition-colors group"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            data-testid="chatbot-toggle"
          >
            <MessageCircle className="h-6 w-6" />
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#D4AF37] rounded-full flex items-center justify-center">
              <Sparkles className="h-2.5 w-2.5 text-[#004534]" />
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 right-6 z-50 w-[400px] max-w-[calc(100vw-48px)] h-[600px] max-h-[calc(100vh-100px)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
            data-testid="chatbot-window"
          >
            {/* Header */}
            <div className="bg-[#004534] text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center relative">
                  <Bot className="h-5 w-5 text-[#004534]" />
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 border-2 border-[#004534] rounded-full"></span>
                </div>
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    CriLam AI Assistant
                    <Sparkles className="h-3.5 w-3.5 text-[#D4AF37]" />
                  </h3>
                  <p className="text-xs text-white/70">Powered by AI • Hindi & English</p>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
                data-testid="chatbot-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[85%] ${message.type === 'user' ? 'order-2' : 'order-1'}`}>
                    <div className={`flex items-end gap-2 ${message.type === 'user' ? 'flex-row-reverse' : ''}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        message.type === 'user' ? 'bg-[#D4AF37]' : 'bg-[#004534]'
                      }`}>
                        {message.type === 'user' ? (
                          <User className="h-4 w-4 text-[#004534]" />
                        ) : (
                          <Bot className="h-4 w-4 text-white" />
                        )}
                      </div>
                      <div className={`p-3 rounded-2xl ${
                        message.type === 'user' 
                          ? 'bg-[#004534] text-white rounded-br-md' 
                          : 'bg-white text-gray-800 rounded-bl-md shadow-sm border border-gray-100'
                      }`}>
                        <div className="text-sm">
                          {formatMessage(message.text)}
                        </div>
                      </div>
                    </div>
                    
                    {/* Quick Options */}
                    {message.type === 'bot' && message.options && (
                      <div className="mt-3 ml-10 flex flex-wrap gap-2">
                        {message.options.map((option, i) => (
                          <button
                            key={i}
                            onClick={() => handleOptionClick(option)}
                            className="px-3 py-1.5 bg-[#004534]/10 text-[#004534] text-xs rounded-full hover:bg-[#004534] hover:text-white transition-colors"
                            data-testid={`quick-option-${i}`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
              
              {/* Typing Indicator */}
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-2"
                >
                  <div className="w-8 h-8 rounded-full bg-[#004534] flex items-center justify-center">
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                  <div className="bg-white p-3 rounded-2xl rounded-bl-md shadow-sm border border-gray-100">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-[#004534] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-[#004534] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-[#004534] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 bg-white border-t border-gray-200">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !isTyping && handleSend()}
                  placeholder="Type your message... (Hindi/English)"
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-full text-sm focus:outline-none focus:border-[#004534] focus:ring-1 focus:ring-[#004534]"
                  disabled={isTyping}
                  data-testid="chatbot-input"
                />
                <button
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isTyping}
                  className="w-10 h-10 bg-[#004534] text-white rounded-full flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#004534] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="chatbot-send"
                >
                  {isTyping ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </button>
              </div>
              <p className="text-[10px] text-gray-400 text-center mt-2 flex items-center justify-center gap-1">
                <Sparkles className="h-3 w-3" />
                CriLam AI Assistant • Powered by GPT
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIChatbot;
